import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { List } from 'immutable';
import { Toggles } from '../../../features-data/sub-models/toggles.model';
import { I2gNgTrackingActionBuilder } from 'i2g-ng-tracking';
import {
  ITrackingAction,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import {
  APPOINTMENTS_MASTER_ROUTE,
  CLIENTS_MASTER_ROUTE,
  CREDIT_MEMOS_MASTER_ROUTE,
  ESTIMATES_MASTER_ROUTE,
  EXPENSES_MASTER_ROUTE,
  HELP_MASTER_ROUTE,
  HOME_MASTER_ROUTE,
  INSTANT_WEBSITE_MASTER_ROUTE,
  INVOICES_MASTER_ROUTE,
  ITEMS_MASTER_ROUTE,
  PURCHASE_ORDERS_MASTER_ROUTE,
  REFER_A_FRIEND_MASTER_ROUTE,
  REPORTS_MASTER_ROUTE,
  SETTINGS_MASTER_ROUTE,
  TRACKED_TIMES_MASTER_ROUTE,
} from '../../main.data';

export interface INavItem {
  label: string;
  route: string[];
  trackingAction: ITrackingAction;
  trackingAdditionalProperties?: any;
  e2eId?: string;
  metadata?: {
    customClass?: string;
    value: string;
  };
}

@Injectable()
export class MainBuilder {
  constructor(
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _translateService: TranslateService
  ) {}

  buildNavItemGroups(featureToggles: Toggles): List<List<INavItem>> {
    const trackingScreenName: TrackingScreenNamesEnum =
      TrackingScreenNamesEnum.navigation;

    // feature toggles
    const isIaNavEnabled = featureToggles.isAllAccess(
      Toggles.KEYS.IA_NAV_EXPERIMENT
    );
    const showAppointments = !featureToggles.isForbidden(
      Toggles.KEYS.APPOINTMENTS
    );
    const showTimeTracking = !featureToggles.isForbidden(
      Toggles.KEYS.TIME_TRACKING
    );
    const showReports =
      !featureToggles.isForbidden(Toggles.KEYS.REPORTS) &&
      !featureToggles.isNoAccess(Toggles.KEYS.REPORTS);
    const showReferAFriend =
      !featureToggles.isForbidden(Toggles.KEYS.REFER_A_FRIEND) &&
      !featureToggles.isNoAccess(Toggles.KEYS.REFER_A_FRIEND);

    const showInstantWebsite = featureToggles.isAllAccess(
      Toggles.KEYS.INSTANT_WEBSITE
    );
    if (isIaNavEnabled) {
      return List([
        [
          {
            label: 'MAIN.MENU.HOME',
            route: [HOME_MASTER_ROUTE],
            trackingAction: this._trackingActionBuilder.buildButtonTapped(
              'home',
              trackingScreenName
            ),
          },
          {
            label: 'MAIN.MENU.CLIENTS',
            route: [CLIENTS_MASTER_ROUTE],
            e2eId: 'side-nav-clients',
            trackingAction: this._trackingActionBuilder.buildButtonTapped(
              'client_list',
              trackingScreenName
            ),
          },
        ],
        [
          {
            label: 'MAIN.MENU.INVOICES',
            route: [INVOICES_MASTER_ROUTE],
            e2eId: 'side-nav-invoices',
            trackingAction: this._trackingActionBuilder.buildButtonTapped(
              'invoice_list',
              trackingScreenName
            ),
          },
          {
            label: 'MAIN.MENU.ESTIMATES',
            route: [ESTIMATES_MASTER_ROUTE],
            trackingAction: this._trackingActionBuilder.buildButtonTapped(
              'estimate_list',
              trackingScreenName
            ),
          },
          {
            label: 'MAIN.MENU.PURCHASE_ORDERS',
            route: [PURCHASE_ORDERS_MASTER_ROUTE],
            trackingAction: this._trackingActionBuilder.buildButtonTapped(
              'purchase_order_list',
              trackingScreenName
            ),
          },
          {
            label: 'MAIN.MENU.CREDIT_MEMOS',
            route: [CREDIT_MEMOS_MASTER_ROUTE],
            trackingAction: this._trackingActionBuilder.buildButtonTapped(
              'credit_memo_list',
              trackingScreenName
            ),
          },
        ],
        [
          {
            label: 'MAIN.MENU.ITEMS',
            route: [ITEMS_MASTER_ROUTE],
            e2eId: 'side-nav-items',
            trackingAction: this._trackingActionBuilder.buildButtonTapped(
              'products_list',
              trackingScreenName
            ),
          },
          {
            label: 'MAIN.MENU.EXPENSES',
            route: [EXPENSES_MASTER_ROUTE],
            e2eId: 'side-nav-expenses',
            trackingAction: this._trackingActionBuilder.buildButtonTapped(
              'expenses_list',
              trackingScreenName
            ),
          },
          showAppointments
            ? {
                label: 'MAIN.MENU.APPOINTMENTS',
                route: [APPOINTMENTS_MASTER_ROUTE],
                e2eId: 'side-nav-appointments',
                trackingAction: this._trackingActionBuilder.buildButtonTapped(
                  'appointments',
                  trackingScreenName
                ),
              }
            : null,
          showTimeTracking
            ? {
                label: 'MAIN.MENU.TIME_TRACKING',
                route: [TRACKED_TIMES_MASTER_ROUTE],
                e2eId: 'side-nav-tracked-times',
                trackingAction: this._trackingActionBuilder.buildButtonTapped(
                  'time_tracking',
                  trackingScreenName
                ),
              }
            : null,
        ],
        showInstantWebsite
          ? [
              {
                label: 'MAIN.MENU.INSTANT_WEBSITE',
                route: [INSTANT_WEBSITE_MASTER_ROUTE],
                trackingAction: this._trackingActionBuilder.buildButtonTapped(
                  'instant_website',
                  trackingScreenName
                ),
              },
            ]
          : null,
        showReports
          ? [
              {
                label: 'MAIN.MENU.REPORTS',
                route: [REPORTS_MASTER_ROUTE],
                trackingAction: this._trackingActionBuilder.buildButtonTapped(
                  'reports',
                  trackingScreenName
                ),
              },
            ]
          : null,
        [
          {
            label: 'MAIN.MENU.HELP',
            route: [HELP_MASTER_ROUTE],
            trackingAction: this._trackingActionBuilder.buildButtonTapped(
              'help',
              trackingScreenName
            ),
          },
          showReferAFriend
            ? {
                label: 'MAIN.MENU.REFER_A_FRIEND',
                route: [REFER_A_FRIEND_MASTER_ROUTE],
                e2eId: 'side-nav-refer-a-friend',
                trackingAction: this._trackingActionBuilder.buildButtonTapped(
                  'refer_a_friend',
                  trackingScreenName
                ),
              }
            : null,
        ],
      ])
        .filter((x) => !!x)
        .map((navGroup: INavItem[]) =>
          List(navGroup).filter((x) => !!x)
        ) as List<List<INavItem>>;
    } else {
      // nav groups
      const homeNavGroup = [
        {
          label: 'MAIN.MENU.HOME',
          route: [HOME_MASTER_ROUTE],
          trackingAction: this._trackingActionBuilder.buildButtonTapped(
            'home',
            trackingScreenName
          ),
        },
      ];
      const documentsNavGroup = [
        {
          label: 'MAIN.MENU.INVOICES',
          route: [INVOICES_MASTER_ROUTE],
          e2eId: 'side-nav-invoices',
          trackingAction: this._trackingActionBuilder.buildButtonTapped(
            'invoice_list',
            trackingScreenName
          ),
        },
        {
          label: 'MAIN.MENU.ESTIMATES',
          route: [ESTIMATES_MASTER_ROUTE],
          trackingAction: this._trackingActionBuilder.buildButtonTapped(
            'estimate_list',
            trackingScreenName
          ),
        },
        {
          label: 'MAIN.MENU.PURCHASE_ORDERS',
          route: [PURCHASE_ORDERS_MASTER_ROUTE],
          trackingAction: this._trackingActionBuilder.buildButtonTapped(
            'purchase_order_list',
            trackingScreenName
          ),
        },
        {
          label: 'MAIN.MENU.CREDIT_MEMOS',
          route: [CREDIT_MEMOS_MASTER_ROUTE],
          trackingAction: this._trackingActionBuilder.buildButtonTapped(
            'credit_memo_list',
            trackingScreenName
          ),
        },
      ];
      const producstNavGroup = [
        {
          label: 'MAIN.MENU.CLIENTS',
          route: [CLIENTS_MASTER_ROUTE],
          e2eId: 'side-nav-clients',
          trackingAction: this._trackingActionBuilder.buildButtonTapped(
            'client_list',
            trackingScreenName
          ),
        },
        {
          label: 'MAIN.MENU.ITEMS',
          route: [ITEMS_MASTER_ROUTE],
          e2eId: 'side-nav-items',
          trackingAction: this._trackingActionBuilder.buildButtonTapped(
            'products_list',
            trackingScreenName
          ),
        },
        {
          label: 'MAIN.MENU.EXPENSES',
          route: [EXPENSES_MASTER_ROUTE],
          e2eId: 'side-nav-expenses',
          trackingAction: this._trackingActionBuilder.buildButtonTapped(
            'expenses_list',
            trackingScreenName
          ),
        },
        showAppointments
          ? {
              label: 'MAIN.MENU.APPOINTMENTS',
              route: [APPOINTMENTS_MASTER_ROUTE],
              e2eId: 'side-nav-appointments',
              trackingAction: this._trackingActionBuilder.buildButtonTapped(
                'appointments',
                trackingScreenName
              ),
            }
          : null,
        showTimeTracking
          ? {
              label: 'MAIN.MENU.TIME_TRACKING',
              route: [TRACKED_TIMES_MASTER_ROUTE],
              e2eId: 'side-nav-tracked-times',
              trackingAction: this._trackingActionBuilder.buildButtonTapped(
                'time_tracking',
                trackingScreenName
              ),
            }
          : null,
      ];
      const instantWebsiteNavGroup = showInstantWebsite
        ? [
            {
              label: 'MAIN.MENU.INSTANT_WEBSITE',
              route: [INSTANT_WEBSITE_MASTER_ROUTE],
              trackingAction: this._trackingActionBuilder.buildButtonTapped(
                'instant_website',
                trackingScreenName
              ),
            },
          ]
        : null;
      const reportsNavGroup = showReports
        ? [
            {
              label: 'MAIN.MENU.REPORTS',
              route: [REPORTS_MASTER_ROUTE],
              trackingAction: this._trackingActionBuilder.buildButtonTapped(
                'reports',
                trackingScreenName
              ),
            },
          ]
        : null;
      const settingsNavGroup = [
        showReferAFriend
          ? {
              label: 'MAIN.MENU.REFER_A_FRIEND',
              route: [REFER_A_FRIEND_MASTER_ROUTE],
              e2eId: 'side-nav-refer-a-friend',
              trackingAction: this._trackingActionBuilder.buildButtonTapped(
                'refer_a_friend',
                trackingScreenName
              ),
            }
          : null,
        {
          label: 'MAIN.MENU.SETTINGS',
          route: [SETTINGS_MASTER_ROUTE],
          e2eId: 'side-nav-settings',
          trackingAction: this._trackingActionBuilder.buildButtonTapped(
            'settings',
            trackingScreenName
          ),
        },
        {
          label: 'MAIN.MENU.HELP',
          route: [HELP_MASTER_ROUTE],
          trackingAction: this._trackingActionBuilder.buildButtonTapped(
            'help',
            trackingScreenName
          ),
        },
      ];
      const navGroups = List([
        homeNavGroup,
        documentsNavGroup,
        producstNavGroup,
        instantWebsiteNavGroup,
        reportsNavGroup,
        settingsNavGroup,
      ]);
      return navGroups
        .filter((x) => !!x)
        .map((navGroup: INavItem[]) =>
          List(navGroup).filter((x) => !!x)
        ) as List<List<INavItem>>;
    }
  }
}
